type EventTarget = Window | Document | HTMLElement;

interface EventConfig {
    name: string;
    isStop: boolean;
    isPrevent: boolean;
    isCapture: boolean;
    isSelf: boolean;
    isOnce: boolean;
    isPassive: boolean;
}

export class EventModifiersPlugin {

    private supportsPassive: boolean;

    constructor() { this.supportsPassive = this.detectPassiveSupport(); }

    public addEventListener(
        element: HTMLElement,
        higherOrderEventName: string,
        handler: (event: Event) => void
    ): () => void {
        const eventConfig = this.parseHigherOrderEventName(higherOrderEventName);
        return (this.setupEventBinding(element, eventConfig, handler));
    }

    public addGlobalEventListener(
        higherOrderElement: string,
        higherOrderEventName: string,
        handler: (event: Event) => void
    ): () => void {
        const target = this.parseHigherOrderElement(higherOrderElement);
        const eventConfig = this.parseHigherOrderEventName(higherOrderEventName);
        return (this.setupEventBinding(target, eventConfig, handler));
    }

    public supports(eventName: string): boolean {
        const eventPattern = /^[a-z]+(?:\.(?:stop|prevent|capture|self|once|passive))+$/;
        return (eventPattern.test(eventName));
    }

    private detectPassiveSupport(): boolean {
        let support = false;
        try {
            const handler = () => { };
            const options: any = {
                get passive(): boolean {
                    return (support = true);
                }
            };
            window.addEventListener('test', handler, options);
            window.removeEventListener('test', handler, options);

        } catch (error) { }
        return (support);
    }

    private parseHigherOrderElement(selector: string): EventTarget {

        switch (selector) {
            case 'window':
                return (window);
            case 'document':
                return (document);
            case 'body':
                return (document.body);
            default:
                throw (new Error(`Element selector [${selector}] not supported.`));
        }

    }

    private parseHigherOrderEventName(eventName: string): EventConfig {
        const parts = eventName.split('.');
        const config = {
            name: parts.shift() as string, // Telling TypeScript not to worry.
            isStop: false,
            isPrevent: false,
            isCapture: false,
            isSelf: false,
            isOnce: false,
            isPassive: false
        };

        while (parts.length) {
            switch (parts.shift()) {
                case 'stop':
                    config.isStop = true;
                    break;
                case 'prevent':
                    config.isPrevent = true;
                    break;
                case 'capture':
                    config.isCapture = true;
                    break;
                case 'self':
                    config.isSelf = true;
                    break;
                case 'once':
                    config.isOnce = true;
                    break;
                case 'passive':
                    config.isPassive = true;
                    break;
                default:
                    throw (new Error(`Event config [${eventName}] not supported.`));
            }

        }
        return (config);
    }

    private setupEventBinding(
        target: EventTarget,
        eventConfig: EventConfig,
        handler: (event: Event) => void
    ): () => void {

        let options: any = eventConfig.isCapture;
        if (this.supportsPassive && eventConfig.isPassive) {
            options = {
                passive: true,
                capture: eventConfig.isCapture
            };
        }
        addProxyFunction();
        return (removeProxyFunction);

        function addProxyFunction(): void {
            target.addEventListener(eventConfig.name, proxyFunction, options);
        }

        function removeProxyFunction(): void {
            target.removeEventListener(eventConfig.name, proxyFunction, options);
        }

        function proxyFunction(event: Event): void {
            if (eventConfig.isSelf && (event.target !== target)) { return; }
            if (eventConfig.isOnce) {
                removeProxyFunction();
            }
            if (eventConfig.isStop) {
                event.stopPropagation();
            }
            if (eventConfig.isPrevent) {
                event.preventDefault();
            }
            handler(event);
        }

    }
}
